import { useEffect, useMemo, useRef } from "react"

import {
  NotebookCellView,
  resolveCoviEntityHref,
  useCoviEntityLinkPlugins,
} from "@/app-covi"
import Heading, { type HeadingOptions } from "@tiptap/extension-heading"
import Link from "@tiptap/extension-link"
import { TaskItem, TaskList } from "@tiptap/extension-list"
import { TableKit } from "@tiptap/extension-table"
import TextAlign from "@tiptap/extension-text-align"
import { TextStyleKit } from "@tiptap/extension-text-style"
import {
  EditorContent,
  useEditor,
  type Editor,
  type JSONContent,
} from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"

import {
  cleanCanvas,
  NOTEBOOK_FONTS,
  notebookCanvas,
  type NotebookAppearance,
} from "../utils/notebookCanvas"
import { cellsFromSnapshot, type Notebook } from "../utils/notebookSnapshot"
import styles from "./NotebookCanvas.module.css"
import { NotebookCanvasContext } from "./NotebookCanvasContext"
import {
  NotebookChart,
  NotebookColumn,
  NotebookColumns,
  NotebookDetails,
  NotebookDetailsContent,
  NotebookDetailsSummary,
  NotebookEvidence,
  NotebookImage,
} from "./NotebookCanvasNodes"
import { NotebookFontFamily, NotebookHighlight } from "./NotebookTextStyle"
import { NotebookVideo } from "./NotebookVideo"

// A canvas renders under a heading it does not own: the page's h1 (the
// breadcrumb, or a shared page's own heading), or a dialog's h2 title. Each
// saved heading level renders that many levels down. `data-level` keeps the
// saved level for styling and for parsing a copied heading back to the level
// it came from.
const NotebookHeading = Heading.extend<HeadingOptions & { offset: number }>({
  addOptions() {
    return { ...(this.parent?.() as HeadingOptions), offset: 1 }
  },
  parseHTML() {
    return this.options.levels.map((level) => ({
      tag: `h${level}`,
      getAttrs: (element: HTMLElement) => ({
        level: Number(element.dataset.level) || level,
      }),
    }))
  },
  renderHTML({ node, HTMLAttributes }) {
    const level = Number(node.attrs.level)
    return [
      `h${Math.min(level + this.options.offset, 6)}`,
      {
        ...this.options.HTMLAttributes,
        ...HTMLAttributes,
        "data-level": level,
      },
      0,
    ]
  },
})

const noop = () => {}
const settled = () => Promise.resolve({ ok: true, detail: "Saved chart" })
export function NotebookCanvas({
  notebook,
  organizationSlug,
  workspaceId,
  content,
  appearance,
  editable = false,
  headingOffset = 1,
  onChange,
  onEditor,
  onUndo,
  onRedo,
}: {
  notebook: Notebook
  organizationSlug: string
  workspaceId: string
  content?: JSONContent
  appearance?: NotebookAppearance
  editable?: boolean
  /** Levels between a saved heading and the one it renders: 1 under a page h1, 2 under a dialog title. */
  headingOffset?: 1 | 2
  onChange?: (content: JSONContent) => void
  onEditor?: (editor: Editor | null) => void
  onUndo?: () => void
  onRedo?: () => void
}) {
  const initialized = useRef(false)
  const change = useRef(onChange)
  change.current = onChange
  const value = content ?? notebookCanvas(notebook)
  const look = appearance ?? notebook.document?.appearance ?? {}
  const extensions = useMemo(
    () => [
      StarterKit.configure({ heading: false, link: false }),
      NotebookHeading.configure({ offset: headingOffset }),
      Link.extend({
        renderHTML({ HTMLAttributes }) {
          const href = String(HTMLAttributes.href ?? "")
          const resolved = resolveCoviEntityHref(
            href,
            organizationSlug,
            workspaceId,
          )
          if (!resolved) {
            return ["span", {}, 0]
          }
          return [
            "a",
            {
              ...HTMLAttributes,
              href: resolved,
              target: "_blank",
              rel: "noopener noreferrer",
            },
            0,
          ]
        },
      }).configure({
        openOnClick: false,
        protocols: ["coval"],
        autolink: true,
      }),
      TableKit,
      TaskList,
      TaskItem,
      TextStyleKit.configure({
        backgroundColor: false,
        lineHeight: false,
        fontFamily: false,
      }),
      NotebookFontFamily,
      NotebookHighlight,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      NotebookColumns,
      NotebookColumn,
      NotebookDetails,
      NotebookDetailsSummary,
      NotebookDetailsContent,
      NotebookChart,
      NotebookEvidence,
      NotebookImage,
      NotebookVideo,
    ],
    [headingOffset, organizationSlug, workspaceId],
  )
  const editor = useEditor(
    {
      extensions,
      content: value,
      editable,
      immediatelyRender: false,
      onCreate: () => {
        initialized.current = true
      },
      onUpdate: ({ editor: current }) => {
        if (initialized.current) {
          change.current?.(cleanCanvas(current.getJSON()))
        }
      },
      editorProps: {
        attributes: {
          "aria-label": "Notebook document",
          role: "textbox",
          "aria-multiline": "true",
        },
      },
    },
    [extensions],
  )
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      editor.setEditable(editable, false)
      editor.view.dom.setAttribute("aria-readonly", String(!editable))
    }
  }, [editor, editable])
  useEffect(() => {
    if (
      editor &&
      !editor.isDestroyed &&
      JSON.stringify(cleanCanvas(editor.getJSON())) !==
        JSON.stringify(cleanCanvas(value))
    ) {
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [editor, value])
  useEffect(() => {
    onEditor?.(editor)
    return () => onEditor?.(null)
  }, [editor, onEditor])
  const cells = useMemo(
    () => cellsFromSnapshot(notebook.id, notebook.snapshot),
    [notebook.id, notebook.snapshot],
  )
  const plugins = useCoviEntityLinkPlugins(organizationSlug, workspaceId)
  const context = useMemo(
    () => ({
      assets: notebook.assets ?? {},
      renderEvidence: (index: number) => {
        const cell = cells[index]
        return cell && !notebook.document?.excluded_cells?.includes(index) ? (
          <NotebookCellView
            cell={cell}
            remarkPlugins={plugins}
            executeProposal={settled}
            updateActionItem={noop}
          />
        ) : null
      },
    }),
    [notebook.assets, notebook.document?.excluded_cells, cells, plugins],
  )
  return (
    <NotebookCanvasContext.Provider value={context}>
      <article
        // Print looks the canvas up by id; a slide excerpt is not the document.
        data-notebook-canvas={content ? undefined : notebook.id}
        className={`${styles.canvas} mx-auto w-full max-w-6xl rounded-lg border border-coval-border bg-surface-default p-8 text-coval-text shadow-sm`}
        style={{
          backgroundColor: look.background,
          color: look.color,
          fontFamily: look.font ? NOTEBOOK_FONTS[look.font] : undefined,
          fontSize: look.fontSize ? `${look.fontSize}px` : undefined,
          lineHeight: 1.65,
        }}
        onKeyDownCapture={(event) => {
          if (!editable || !(event.metaKey || event.ctrlKey)) {
            return
          }
          if (event.key.toLowerCase() === "z") {
            event.preventDefault()
            event.stopPropagation()
            if (event.shiftKey) {
              onRedo?.()
            } else {
              onUndo?.()
            }
          }
          if (event.key.toLowerCase() === "y") {
            event.preventDefault()
            event.stopPropagation()
            onRedo?.()
          }
        }}
      >
        <EditorContent editor={editor} />
      </article>
    </NotebookCanvasContext.Provider>
  )
}
